import React, { useState, useEffect } from 'react';
import { UserCheck, Clock, Edit3, CheckCircle, XCircle, Calendar, Building, MapPin, Briefcase, Save, X, Laptop, GraduationCap, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { User } from '../types';
import { assignMentor, createItResourceRequest, getMentionableUsers, getOnboardingRecords, saveOnboardingRecord, submitMentorPrep, updateOnboardingStatus } from '../api';
import { formatBeijingTime } from '../utils/dateFormat';

interface Props {
  currentUser: User;
  notificationBell?: React.ReactNode;
  onOpenCandidate?: (id: string) => void;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: any }> = {
  pending: { label: '待入职', color: 'text-gray-600', bg: 'bg-gray-50', icon: Clock },
  confirmed: { label: '已确认', color: 'text-primary-600', bg: 'bg-primary-50', icon: CheckCircle },
  onboarded: { label: '已入职', color: 'text-primary-600', bg: 'bg-primary-50', icon: CheckCircle },
  cancelled: { label: '已取消', color: 'text-gray-500', bg: 'bg-gray-100', icon: XCircle },
};

const EMPLOYMENT_TYPES: Record<string, string> = {
  full_time: '全职', part_time: '兼职', intern: '实习', outsource: '外包', labor_dispatch: '劳务派遣',
};

export default function OnboardingManager({ currentUser, notificationBell, onOpenCandidate }: Props) {
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<any>({});
  const [itRequestId, setItRequestId] = useState<string | null>(null);
  const [itForm, setItForm] = useState<any>({ notes: '', expected_onboard_date: '', urgency: 'normal' });
  const [saving, setSaving] = useState(false);
  // Mentor assignment
  const [mentorPickerId, setMentorPickerId] = useState<string | null>(null);
  const [mentorUsers, setMentorUsers] = useState<User[]>([]);
  const [selectedMentorId, setSelectedMentorId] = useState('');
  const [assigningMentor, setAssigningMentor] = useState(false);
  // Mentor prep
  const [mentorPrepId, setMentorPrepId] = useState<string | null>(null);
  const [prepNotes, setPrepNotes] = useState('');
  const [submittingPrep, setSubmittingPrep] = useState(false);
  // Expanded prep notes view
  const [expandedPrep, setExpandedPrep] = useState<Set<string>>(new Set());

  const isHr = ['admin', 'hr', 'vip'].includes(currentUser.role);

  const fetchData = () => {
    setLoading(true);
    getOnboardingRecords().then(setRecords).catch(() => toast.error('加载失败')).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = filter === 'all' ? records : records.filter(r => r.status === filter);

  const statusCounts = records.reduce((acc: Record<string, number>, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  const startEdit = (record: any) => {
    setEditingId(record.candidate_id);
    setEditForm({
      candidate_id: record.candidate_id,
      position_name: record.position_name || record.candidate_position_name || '',
      department: record.department || record.position_department || '',
      report_to: record.report_to || '',
      work_city: record.work_city || record.expected_city || '',
      onboard_date: record.onboard_date || '',
      employment_type: record.employment_type || 'full_time',
      probation_months: record.probation_months ?? 3,
      salary_info: record.salary_info || '',
      notes: record.notes || '',
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveOnboardingRecord(editForm);
      toast.success('入职信息已保存');
      setEditingId(null);
      fetchData();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '保存失败');
    } finally { setSaving(false); }
  };

  const handleStatusChange = async (id: string, status: string) => {
    const labels: Record<string, string> = { confirmed: '确认入职', onboarded: '标记已入职', cancelled: '取消入职' };
    if (!window.confirm(`确认${labels[status] || status}？`)) return;
    try {
      await updateOnboardingStatus(id, status);
      toast.success('状态已更新');
      fetchData();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '操作失败');
    }
  };

  const openMentorPicker = async (record: any) => {
    setMentorPickerId(record.candidate_id);
    setSelectedMentorId(record.mentor_id || '');
    if (mentorUsers.length === 0) {
      const all = await getMentionableUsers().catch(() => []);
      setMentorUsers((all as User[]).filter(u => u.role !== 'referrer' && u.status === 'active'));
    }
  };

  const handleAssignMentor = async (candidateId: string) => {
    if (!selectedMentorId) { toast.error('请选择导师'); return; }
    setAssigningMentor(true);
    try {
      const res = await assignMentor(candidateId, selectedMentorId);
      toast.success(res.message || '导师已分配');
      setMentorPickerId(null);
      fetchData();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '分配失败');
    } finally { setAssigningMentor(false); }
  };

  const openMentorPrep = (record: any) => {
    setMentorPrepId(record.candidate_id);
    setPrepNotes(record.mentor_prep_notes || '');
  };

  const handleSubmitPrep = async (candidateId: string) => {
    if (!prepNotes.trim()) { toast.error('请填写带教准备内容'); return; }
    setSubmittingPrep(true);
    try {
      const res = await submitMentorPrep(candidateId, prepNotes);
      toast.success(res.message || '带教准备已提交');
      setMentorPrepId(null);
      fetchData();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '提交失败');
    } finally { setSubmittingPrep(false); }
  };

  const startItRequest = (record: any) => {
    setItRequestId(record.candidate_id);
    setItForm({
      candidate_id: record.candidate_id,
      notes: '',
      expected_onboard_date: record.onboard_date || '',
      urgency: 'normal',
    });
  };

  const handleCreateItRequest = async () => {
    try {
      setSaving(true);
      const result = await createItResourceRequest(itForm);
      toast.success(result.message || 'IT资源申请已发起');
      setItRequestId(null);
      setItForm({ notes: '', expected_onboard_date: '', urgency: 'normal' });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '发起IT资源申请失败');
    } finally { setSaving(false); }
  };

  return (
    <div className="h-full flex flex-col gap-2.5">
      {/* Header */}
      <div className="shrink-0 bg-white rounded-2xl border border-gray-200/80 px-6 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <UserCheck size={20} className="text-primary-500" />
            待入职管理
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            管理候选人入职流程 · 共 {records.length} 人
            {statusCounts.pending ? ` · ${statusCounts.pending} 人待入职` : ''}
          </p>
        </div>
        {notificationBell}
      </div>

      {/* Filter tabs */}
      <div className="shrink-0 bg-white rounded-2xl border border-gray-200/80 px-6 py-2 flex items-center gap-1">
        {[['all', '全部'], ['pending', '待入职'], ['confirmed', '已确认'], ['onboarded', '已入职'], ['cancelled', '已取消']].map(([key, label]) => (
          <button key={key} onClick={() => setFilter(key)}
            className={clsx('px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
              filter === key ? 'bg-primary-50 text-primary-700' : 'text-gray-500 hover:bg-gray-50')}>
            {label}
            {key !== 'all' && statusCounts[key] ? (
              <span className="ml-1 text-[10px] text-gray-400">({statusCounts[key]})</span>
            ) : null}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
          <UserCheck size={48} strokeWidth={1} className="text-gray-300 mb-3" />
          <p className="text-sm">
            {records.length === 0 ? '暂无入职记录' : '当前筛选条件下暂无记录'}
          </p>
          {records.length === 0 && (
            <p className="text-xs text-gray-300 mt-2 max-w-sm text-center">
              当候选人Offer审批通过并进入"待入职"阶段后，系统会自动创建入职记录
            </p>
          )}
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="space-y-2.5">
            {filtered.map(r => {
              const sc = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending;
              const StatusIcon = sc.icon;
              const isEditing = editingId === r.candidate_id;

              return (
                <div key={r.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  {/* Card header */}
                  <div className="px-5 py-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-sm font-bold text-primary-600 shrink-0">
                      {(r.name || '?').charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[13px] font-bold text-gray-800 cursor-pointer hover:text-primary-600"
                          onClick={() => onOpenCandidate?.(r.candidate_id)}>{r.name}</span>
                        <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium inline-flex items-center gap-0.5', sc.bg, sc.color)}>
                          <StatusIcon size={10} />{sc.label}
                        </span>
                        {(r.position_name || r.candidate_position_name) && (
                          <span className="text-[10px] text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded-full">
                            {r.position_name || r.candidate_position_name}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-400 mt-1 flex items-center gap-2 flex-wrap">
                        {r.school && <span>{r.school}</span>}
                        {r.education && <span>· {r.education}</span>}
                        {r.latest_company && <span>· {r.latest_company}</span>}
                        {r.offer_approved_at && <span>· Offer通过: {formatBeijingTime(r.offer_approved_at, 'date')}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {r.onboard_date && (
                        <div className="text-[11px] font-medium text-primary-600 bg-primary-50 px-2.5 py-1 rounded flex items-center gap-1">
                          <Calendar size={11} />入职: {r.onboard_date}
                        </div>
                      )}
                      {isHr && r.status !== 'onboarded' && r.status !== 'cancelled' && (
                        <button onClick={() => isEditing ? setEditingId(null) : startEdit(r)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" title="编辑">
                          {isEditing ? <X size={15} /> : <Edit3 size={15} />}
                        </button>
                      )}
                      {isHr && r.status !== 'cancelled' && (
                        <button onClick={() => mentorPickerId === r.candidate_id ? setMentorPickerId(null) : openMentorPicker(r)}
                          className={clsx('p-1.5 rounded-lg transition-colors', r.mentor_id ? 'text-primary-500 hover:bg-primary-50' : 'text-gray-400 hover:bg-primary-50 hover:text-primary-600')}
                          title={r.mentor_id ? `导师：${r.mentor_name}` : '分配导师'}>
                          <GraduationCap size={15} />
                        </button>
                      )}
                      {isHr && ['offer', 'pending_onboard', 'onboarded'].includes(r.stage) && (
                        <button onClick={() => itRequestId === r.candidate_id ? setItRequestId(null) : startItRequest(r)}
                          className="p-1.5 rounded-lg hover:bg-primary-50 text-gray-400 hover:text-primary-600 transition-colors" title="发起IT资源申请">
                          <Laptop size={15} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Mentor status bar */}
                  {r.mentor_id && mentorPickerId !== r.candidate_id && (
                    <div className="px-5 py-2 bg-primary-50/60 border-t border-primary-100 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-xs text-primary-700">
                        <GraduationCap size={13} className="shrink-0" />
                        <span className="font-medium">导师：{r.mentor_name}</span>
                        {r.mentor_prep_submitted_at ? (
                          <span className="ml-1 text-[10px] bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded">带教准备已提交</span>
                        ) : (
                          <span className="ml-1 text-[10px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">待填写带教准备</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {(r.mentor_prep_submitted_at && r.mentor_prep_notes) && (
                          <button onClick={() => setExpandedPrep(prev => { const s = new Set(prev); s.has(r.candidate_id) ? s.delete(r.candidate_id) : s.add(r.candidate_id); return s; })}
                            className="text-[11px] text-primary-500 hover:text-primary-700 flex items-center gap-0.5">
                            {expandedPrep.has(r.candidate_id) ? <><ChevronUp size={12}/>收起</> : <><ChevronDown size={12}/>查看准备</>}
                          </button>
                        )}
                        {(r.mentor_id === currentUser.id || isHr) && (
                          <button onClick={() => mentorPrepId === r.candidate_id ? setMentorPrepId(null) : openMentorPrep(r)}
                            className="text-[11px] text-primary-600 hover:text-primary-800 flex items-center gap-0.5 font-medium">
                            <FileText size={11} />{r.mentor_prep_submitted_at ? '修改准备' : '填写准备'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Expanded prep notes */}
                  {r.mentor_prep_notes && expandedPrep.has(r.candidate_id) && (
                    <div className="px-5 py-3 bg-primary-50/30 border-t border-primary-100">
                      <p className="text-xs text-gray-500 mb-1 font-medium">带教准备内容</p>
                      <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">{r.mentor_prep_notes}</p>
                    </div>
                  )}

                  {/* Info grid (always visible for records with data) */}
                  {!isEditing && (r.department || r.report_to || r.work_city || r.employment_type) && (
                    <div className="px-5 pb-3 grid grid-cols-2 md:grid-cols-5 gap-x-4 gap-y-2 text-xs border-t border-gray-50 pt-3">
                      {r.department && <div><span className="text-gray-400">部门</span><div className="font-medium text-gray-700 mt-0.5 flex items-center gap-1"><Building size={11} className="text-gray-400" />{r.department}</div></div>}
                      {r.report_to && <div><span className="text-gray-400">汇报对象</span><div className="font-medium text-gray-700 mt-0.5">{r.report_to}</div></div>}
                      {r.work_city && <div><span className="text-gray-400">工作地点</span><div className="font-medium text-gray-700 mt-0.5 flex items-center gap-1"><MapPin size={11} className="text-gray-400" />{r.work_city}</div></div>}
                      {r.employment_type && <div><span className="text-gray-400">用工类型</span><div className="font-medium text-gray-700 mt-0.5 flex items-center gap-1"><Briefcase size={11} className="text-gray-400" />{EMPLOYMENT_TYPES[r.employment_type] || r.employment_type}</div></div>}
                      {r.probation_months > 0 && <div><span className="text-gray-400">试用期</span><div className="font-medium text-gray-700 mt-0.5">{r.probation_months} 个月</div></div>}
                    </div>
                  )}

                  {/* Status action bar */}
                  {isHr && !isEditing && r.status !== 'onboarded' && r.status !== 'cancelled' && (
                    <div className="px-5 py-2.5 bg-gray-50/50 border-t border-gray-100 flex items-center justify-end gap-2">
                      {r.status === 'pending' && r.onboard_date && (
                        <button onClick={() => handleStatusChange(r.id, 'confirmed')}
                          className="px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors">
                          确认入职信息
                        </button>
                      )}
                      {(r.status === 'pending' || r.status === 'confirmed') && (
                        <>
                          <button onClick={() => handleStatusChange(r.id, 'onboarded')}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
                            标记已入职
                          </button>
                          <button onClick={() => handleStatusChange(r.id, 'cancelled')}
                            className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-200 rounded-lg transition-colors">
                            取消
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  {/* Edit form */}
                  {isEditing && (
                    <div className="px-5 pb-4 border-t border-gray-100 pt-4 space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">应聘岗位</label>
                          <input value={editForm.position_name} onChange={e => setEditForm({ ...editForm, position_name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">所属部门</label>
                          <input value={editForm.department} onChange={e => setEditForm({ ...editForm, department: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">汇报对象</label>
                          <input value={editForm.report_to} onChange={e => setEditForm({ ...editForm, report_to: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">工作地点</label>
                          <input value={editForm.work_city} onChange={e => setEditForm({ ...editForm, work_city: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">入职日期</label>
                          <input type="date" value={editForm.onboard_date} onChange={e => setEditForm({ ...editForm, onboard_date: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">用工类型</label>
                          <select value={editForm.employment_type} onChange={e => setEditForm({ ...editForm, employment_type: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white">
                            {Object.entries(EMPLOYMENT_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">试用期（月）</label>
                          <input type="number" min={0} max={12} value={editForm.probation_months}
                            onChange={e => setEditForm({ ...editForm, probation_months: parseInt(e.target.value) || 0 })}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                        </div>
                        <div className="md:col-span-2">
                          <label className="text-xs text-gray-500 mb-1 block">备注</label>
                          <input value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 pt-1">
                        <button onClick={() => setEditingId(null)}
                          className="px-4 py-2 text-xs text-gray-600 hover:bg-gray-100 rounded-lg">取消</button>
                        <button onClick={handleSave} disabled={saving}
                          className="px-4 py-2 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 flex items-center gap-1">
                          <Save size={12} />{saving ? '保存中...' : '保存'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Mentor picker panel */}
                  {mentorPickerId === r.candidate_id && (
                    <div className="px-5 pb-4 border-t border-primary-100 bg-primary-50/20 pt-4 space-y-3">
                      <p className="text-xs font-semibold text-gray-600 flex items-center gap-1.5"><GraduationCap size={13} className="text-primary-500" />选择导师</p>
                      <div className="max-h-48 overflow-y-auto space-y-1">
                        {mentorUsers.map(u => (
                          <button key={u.id} onClick={() => setSelectedMentorId(u.id)}
                            className={clsx('w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border text-left text-xs transition-all',
                              selectedMentorId === u.id ? 'border-primary-300 bg-primary-50' : 'border-gray-200 hover:border-gray-300 bg-white')}>
                            <div className={clsx('w-4 h-4 rounded-full border-2 shrink-0 transition-colors',
                              selectedMentorId === u.id ? 'border-primary-600 bg-primary-600' : 'border-gray-300')} />
                            <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center text-[10px] font-bold text-primary-600 shrink-0">
                              {(u.display_name || u.username || '').charAt(0)}
                            </div>
                            <div>
                              <span className="font-medium text-gray-800">{u.display_name || u.username}</span>
                              <span className="ml-1.5 text-[10px] text-gray-400">{u.role === 'admin' ? '管理员' : u.role === 'hr' ? 'HR' : u.role === 'vip' ? 'VIP面试官' : '面试官'}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                      <div className="flex justify-end gap-2 pt-1">
                        <button onClick={() => setMentorPickerId(null)} className="px-4 py-2 text-xs text-gray-600 hover:bg-gray-100 rounded-lg">取消</button>
                        <button onClick={() => handleAssignMentor(r.candidate_id)} disabled={!selectedMentorId || assigningMentor}
                          className="px-4 py-2 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 flex items-center gap-1">
                          <GraduationCap size={12} />{assigningMentor ? '分配中...' : '确认分配'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Mentor prep form */}
                  {mentorPrepId === r.candidate_id && (
                    <div className="px-5 pb-4 border-t border-primary-100 bg-primary-50/20 pt-4 space-y-3">
                      <p className="text-xs font-semibold text-gray-600 flex items-center gap-1.5"><FileText size={13} className="text-primary-500" />带教准备计划</p>
                      <textarea
                        value={prepNotes}
                        onChange={e => setPrepNotes(e.target.value)}
                        rows={5}
                        placeholder={`请填写带教准备内容，例如：\n• 带教目标：帮助新员工快速融入团队，了解业务流程\n• 首周安排：公司介绍 / 业务培训 / 系统权限开通\n• 带教计划：每日 30min 沟通，每周复盘一次\n• 注意事项：...`}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none bg-white"
                      />
                      <div className="flex justify-end gap-2 pt-1">
                        <button onClick={() => setMentorPrepId(null)} className="px-4 py-2 text-xs text-gray-600 hover:bg-gray-100 rounded-lg">取消</button>
                        <button onClick={() => handleSubmitPrep(r.candidate_id)} disabled={!prepNotes.trim() || submittingPrep}
                          className="px-4 py-2 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 flex items-center gap-1">
                          <FileText size={12} />{submittingPrep ? '提交中...' : '提交带教准备'}
                        </button>
                      </div>
                    </div>
                  )}

                  {itRequestId === r.candidate_id && (
                    <div className="px-5 pb-4 border-t border-primary-100 bg-primary-50/30 pt-4 space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">预计入职时间</label>
                          <input type="date" value={itForm.expected_onboard_date} onChange={e => setItForm({ ...itForm, expected_onboard_date: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white" />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">紧急程度</label>
                          <select value={itForm.urgency} onChange={e => setItForm({ ...itForm, urgency: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white">
                            <option value="low">低</option>
                            <option value="normal">普通</option>
                            <option value="high">高</option>
                            <option value="urgent">紧急</option>
                          </select>
                        </div>
                        <div className="md:col-span-3">
                          <label className="text-xs text-gray-500 mb-1 block">备注信息（选填）</label>
                          <input value={itForm.notes} onChange={e => setItForm({ ...itForm, notes: e.target.value })}
                            placeholder="如：需开通邮箱、VPN、研发设备、代码仓库权限等"
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white" />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 pt-1">
                        <button onClick={() => setItRequestId(null)}
                          className="px-4 py-2 text-xs text-gray-600 hover:bg-white rounded-lg">取消</button>
                        <button onClick={handleCreateItRequest} disabled={saving}
                          className="px-4 py-2 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 flex items-center gap-1">
                          <Laptop size={12} />{saving ? '发起中...' : '发起IT资源申请'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
